import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, CircularProgress, Typography } from '@material-ui/core';
import EmailRoundedIcon from '@material-ui/icons/EmailRounded';
import LockRoundedIcon from '@material-ui/icons/LockRounded';
import TrendingUpIcon from '@material-ui/icons/TrendingUp';
import BarChartIcon from '@material-ui/icons/BarChart';
import TrackChangesIcon from '@material-ui/icons/TrackChanges';
import SecurityIcon from '@material-ui/icons/Security';
import { Input } from '@rocketseat/unform';
import { Link } from 'react-router-dom';
import * as Yup from 'yup';

import { signInRequest } from '../../../store/modules/auth/actions';
import { ApplicationState, persistor } from '../../../store';
import {
  Container,
  MarketingSide,
  FormSide,
  FormCard,
  FormLogin,
  useStyles,
} from './LoginStyles';
import logoimage from '../../../files/images/logo.png';
import dashVideo from './Assets/dashDesktop.mp4';

const schema = Yup.object().shape({
  email: Yup.string()
    .email('Insira um e-mail válido')
    .required('O e-mail é obrigatório'),
  password: Yup.string().required('A senha é obrigatória'),
});

export default function Login() {
  const dispatch = useDispatch();
  const classes = useStyles();
  const loading = useSelector((state: ApplicationState) => state.auth.loading);
  const [emailFocus, setEmailFocus] = useState(false);
  const [emailValue, setEmailValue] = useState('');
  const [passwordFocus, setPasswordFocus] = useState(false);
  const [passwordValue, setPasswordValue] = useState('');

  useEffect(() => {
    setTimeout(() => {
      persistor.purge();
    }, 300);
  }, []);

  function handleSubmit(data: any): void {
    const { email, password } = data;
    dispatch(signInRequest(email, password));
  }

  function onInputFocus(type: string): void {
    if (type === 'email') setEmailFocus(true);
    else setPasswordFocus(true);
  }

  function onInputBlur(type: string): void {
    if (type === 'email' && !emailValue) setEmailFocus(false);
    if (type === 'password' && !passwordValue) setPasswordFocus(false);
  }

  return (
    <Container>
      <MarketingSide>
        <img src={logoimage} alt="DashTrader" className={classes.marketingLogo} />
        <div className={classes.heroImageWrapper}>
          <video src={dashVideo} autoPlay loop muted playsInline className={classes.heroImage} />
        </div>
        <Typography className={classes.heroSubtitle}>
          Acompanhe resultados, identifique padrões e evolua como trader com um dashboard completo e intuitivo.
        </Typography>
        <div className={classes.featureList}>
          <div className={classes.featureItem}>
            <div className={classes.featureIcon}><TrendingUpIcon style={{ fontSize: '16px' }} /></div>
            Acompanhamento de lucro e prejuízo em tempo real
          </div>
          <div className={classes.featureItem}>
            <div className={classes.featureIcon}><BarChartIcon style={{ fontSize: '16px' }} /></div>
            Estatísticas detalhadas por ativo, horário e tipo
          </div>
          <div className={classes.featureItem}>
            <div className={classes.featureIcon}><TrackChangesIcon style={{ fontSize: '16px' }} /></div>
            Metas diárias, semanais e mensais com progresso
          </div>
          <div className={classes.featureItem}>
            <div className={classes.featureIcon}><SecurityIcon style={{ fontSize: '16px' }} /></div>
            Stop Win e Stop Loss para proteger seus resultados
          </div>
        </div>
      </MarketingSide>

      <FormSide>
        <FormCard>
          <img src={logoimage} alt="DashTrader" className={classes.logo} />
          <Typography className={classes.welcomeText}>
            Entre na sua conta para acessar o dashboard
          </Typography>

          <FormLogin onSubmit={handleSubmit} schema={schema}>
            <div className="input-wrapper">
              <label>Email</label>
              <div className={`input-field ${emailFocus ? 'focus' : ''}`}>
                <EmailRoundedIcon />
                <Input
                  onFocus={() => onInputFocus('email')}
                  onBlur={() => onInputBlur('email')}
                  name="email"
                  type="email"
                  autoFocus
                  spellCheck={false}
                  placeholder="seu@email.com"
                  onChange={(e: any) => setEmailValue(e.target.value)}
                />
              </div>
            </div>

            <div className="input-wrapper">
              <label>Senha</label>
              <div className={`input-field ${passwordFocus ? 'focus' : ''}`}>
                <LockRoundedIcon />
                <Input
                  onFocus={() => onInputFocus('password')}
                  onBlur={() => onInputBlur('password')}
                  name="password"
                  type="password"
                  placeholder="••••••••"
                  onChange={(e: any) => setPasswordValue(e.target.value)}
                />
              </div>
            </div>

            <Button
              className="submitButton"
              variant="contained"
              color="primary"
              type="submit"
              disabled={!!loading}
            >
              {loading ? <CircularProgress size={22} /> : 'Entrar'}
            </Button>
          </FormLogin>

          <div className={classes.footer}>
            Ainda não possui uma conta?{' '}
            <Link to="/register">Inscrever-se</Link>
          </div>
        </FormCard>
      </FormSide>
    </Container>
  );
}
